import axios from "axios";
import { attachTokenRefreshInterceptors } from "../Utils/authTokenRefresh";

const authAxios = axios.create({
  baseURL: process.env.REACT_APP_AUTH_API_URL,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json, text/plain",
  },
});

attachTokenRefreshInterceptors(authAxios);

export const authAPI = {
  /** POST /api/Client — B2B client signup (public). */
  register: async (form) => {
    try {
      const email = (form.email || "").trim();
      const contactPerson = (form.contactPerson || "").trim();
      const body = {
        clientName: form.clientName,
        bookingRegion: form.bookingRegion,
        accountTypeId: 0,
        appId: 0,
        clientCode: null,
        contactPerson,
        roleInCompany: form.roleInCompany,
        employeeSize: form.employeeSize ?? 0,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        gstNumber: form.gstNumber,
        panNumber: form.panNumber,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2 || "",
        city: form.city,
        state: form.state,
        country: form.country,
        postalCode: form.postalCode,
        creditLimit: 0,
        balanceAmount: 0,
        marginValueDiscount: 0,
        tncValue: true,
        referralCode: form.referralCode || "",
        userName: email,
        password: email,
        email,
        phoneNumber: form.phoneNumber,
        createdBy: contactPerson,
      };

      console.log("POST /api/Client client signup");
      const response = await authAxios.post("/api/Client", body);
      return response.data;
    } catch (error) {
      console.error("Registration API error:", error);
      throw error;
    }
  },
  sendOTP: async (email) => {
    try {
      const response = await authAxios.post("/api/Auth/forgot-password", {
        email: email,
      });

      if (response.data.error) {
        throw {
          message: response.data.message || "Failed to send OTP. Please try again."
        };
      }

      console.log("OTP sent successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("Send OTP error:", error.response || error);
      throw (
        error.response?.data || error || {
          message: "Failed to send OTP. Please try again.",
        }
      );
    }
  },
  registrationVerifyOTP: async (verificationData) => {
    try {
      const response = await authAxios.post("/api/Login/VerifyRegistrationOTP", {
        mobile: verificationData.mobile,
        otp: parseInt(verificationData.otp),
        otpCategoryId: 1
      });

      console.log("OTP verification successful:", response.data);
      return response.data;
    } catch (error) {
      console.error("OTP verification error:", error.response || error);
      throw (
        error.response?.data || {
          message: "OTP verification failed. Please try again.",
        }
      );
    }
  },
  login: async (credentials) => {
    try {
      const response = await authAxios.post("/api/Auth/login", {
        username: credentials.username,
        password: credentials.password
      });

      console.log("Login successful:", response.data);
      return response.data;
    } catch (error) {
      console.error("Login error:", error.response || error);
      throw (
        error.response?.data || {
          message: "Login failed. Please try again.",
        }
      );
    }
  },
  verifyLoginOTP: async (verificationData) => {
    try {
      const response = await authAxios.post("/api/Login/VerifyLoginOTP", {
        mobile: verificationData.mobile,
        otp: parseInt(verificationData.otp),
        otpCategoryId: 2
      });

      console.log("Login OTP verification successful:", response.data);
      return response.data;
    } catch (error) {
      console.error("Login OTP verification error:", error.response || error);
      throw (
        error.response?.data || {
          message: "OTP verification failed. Please try again.",
        }
      );
    }
  },
  savePassword: async (passwordData) => {
    try {
      const response = await authAxios.post("/api/Authentication/SavePassword", {
        userId: passwordData.userId,
        password: passwordData.password,
        OTP: passwordData.OTP
      });

      console.log("Password saved successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("Password save error:", error.response || error);
      throw (
        error.response?.data || {
          message: "Failed to save password. Please try again.",
        }
      );
    }
  },
  changePassword: async (passwordData) => {
    try {
      const response = await authAxios.post("/api/Customer/ChangePassword", {
        userId: passwordData.userId,
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });

      console.log("Password changed successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("Change password error:", error.response || error);
      throw (
        error.response?.data || {
          message: "Failed to change password. Please try again.",
        }
      );
    }
  },
  /** GET /api/User/client/{clientId} — list users for the B2B client (requires bearer token). */
  getClientUsers: async (clientId) => {
    try {
      const response = await authAxios.get(`/api/User/client/${clientId}`);
      return response.data;
    } catch (error) {
      console.error("getClientUsers error:", error.response || error);
      throw error.response?.data || { message: "Failed to fetch users." };
    }
  },
  /** POST /api/User — create user under client (requires bearer token). */
  createUser: async (payload) => {
    try {
      const response = await authAxios.post("/api/User", payload);
      return response.data;
    } catch (error) {
      console.error("createUser error:", error.response || error);
      throw error.response?.data || { message: "Failed to create user." };
    }
  },
  /** GET /api/Client/{clientId}/balance — wallet balance for B2B client (requires bearer token). */
  getClientBalance: async (clientId) => {
    try {
      const response = await authAxios.get(`/api/Client/${clientId}/balance`);
      return response.data;
    } catch (error) {
      console.error("getClientBalance error:", error.response || error);
      throw error.response?.data || { message: "Failed to fetch balance." };
    }
  },
  forgotPassword: async (data) => {
    try {
      const response = await authAxios.post("/api/Auth/reset-password", {
        email: data.email,
        otp: parseInt(data.otp),
        newPassword: data.password,
        confirmPassword: data.password
      });

      if (response.data.error) {
        throw {
          message: response.data.message || "Failed to reset password. Please try again."
        };
      }

      console.log("Password reset successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("Forgot password error:", error.response || error);
      throw (
        error.response?.data || error || {
          message: "Failed to reset password. Please try again.",
        }
      );
    }
  },
}; 